/**
 * eventVocabularies.js
 *
 * Hardcoded vocabulary + requirements matrix for the new 2-step submission
 * wizard prototype. Mirrors what the spec doc says will eventually live in
 * `admin_config` under keys `incidentCategoriesByTreatmentAndCode`,
 * `eventRequirements`, and `attachmentCategories`.
 *
 * Source: event_types_logic.md (see Treatment-specific vocabularies and
 * Requirements matrix sections). Provisional — pending clinical review.
 *
 * When the migration lands, swap these constants for config-driven lookups
 * and delete this file.
 */

import { REFUSAL_REGISTER_CATEGORY, REFUSAL_REGISTER_FUNCTION } from '@shared/refusalRegister.js';

// ── Event types ───────────────────────────────────────────────────────────
export const EVENT_TYPES = [
  { value: 'Treatment',       label: 'Treatment incident', enabled: true,  hint: 'Adverse event arising from a clinical treatment' },
  { value: 'NearMiss',        label: 'Near miss',          enabled: true,  hint: 'A hazard or near-incident caught before harm occurred' },
  { value: 'ClinicIncident',  label: 'Clinic incident',    enabled: true,  hint: 'Non-treatment incident at a clinic — injury, accident, or public liability' },
];

// NOTE: subtypes are now config-driven via the `subtypes` field on
// treatmentTypes (see incidentCategoryCascade.subtypesForTreatment). The old
// hardcoded AWI_SUBTYPES constant has been removed — nothing reads it.

// ── Incident category vocabularies per (treatment, subtype, code) ────────
// Each entry is a list of category strings. 'Other' triggers a free-text field.

const LHR_CATEGORIES = {
  'Code 1': [
    'Rashes (general)',
    'Dermatitis / Fungal',
    'Acne',
    'Other',
  ],
  'Code 2': [
    'Folliculitis',
    'Leucotrichia (white hair)',
    'Paradoxical hypertrichosis',
    'Fox Fordyce',
    'Livedo reticularis',
    'Urticaria',
    'Other',
  ],
  'Code 3': [
    'Laser over mole',
    'Hyper/hypopigmentation',
    'Grazing — singed hair',
    'Heated handpiece',
    'Heated guide tip',
    'Grazing — pencil scratch',
    'Hair line lasered accidentally',
    'Eyebrow lasered',
    'Clothes lasered',
    'Scuffing',
    'Reflection burn on bony area',
    'Burnt make-up / product',
    'Other',
  ],
  'Code 4': [
    'Tattoo lasered over',
    'Other',
  ],
  'Code 5': [
    '755 nm on skin type 4–6',
    '755 nm on tanned skin',
    'Treated outwith parameters',
    '1064 nm burn',
    'Eye injury',
    'Within protocol',
    // Note: no "Other" — pending clinical confirmation
  ],
};

// LHR Code 1 & 2 options shown to reporters at submission time (non–Admin-tier).
// Admin, Supervisor and Super Admin see the full vocabulary when submitting.
// Case-file edits use the full list — only Admin-tier roles can edit cases.
export const LHR_REPORTER_CATEGORY_LIMITS = {
  'Code 1': [
    'Rashes (general)',
    'Dermatitis / Fungal',
    'Acne',
    'Other',
  ],
  'Code 2': [
    'Folliculitis',
    'Other',
  ],
};

/**
 * Narrow LHR Code 1/2 dropdowns for reporters at submission. When fullAccess is
 * true (Admin+), returns categories unchanged.
 */
export function filterLhrReporterCategories(categories, treatment, code, { fullAccess = false } = {}) {
  if (!Array.isArray(categories)) return [];
  if (fullAccess || treatment !== 'LHR' || !LHR_REPORTER_CATEGORY_LIMITS[code]) return categories;
  const allowed = new Set(LHR_REPORTER_CATEGORY_LIMITS[code]);
  return categories.filter(c => allowed.has(c));
}

// AWI Toxin/Filler code criteria mirror the Adverse Event Code Policy
// (BM2797, V2 — eff. 07/03/26). Keep these in sync with that SOP.
const AWI_TOXIN_CATEGORIES = {
  'Code 1': [
    'Reduced or increased movement',
    'Eyebrow asymmetry (Spock brow)',
    'Sensation of heaviness',
  ],
  'Code 2': [
    'Severe heaviness',
  ],
  'Code 3': [
    'Upset or dissatisfied patient',
  ],
  'Code 4': [
    'Eyelid or brow ptosis',
  ],
  'Code 5': [
    'Nausea',
    'Dizziness',
    'Visual disturbance',
    'Allergic reaction',
    'Infection',
  ],
};

const AWI_FILLER_CATEGORIES = {
  'Code 1': [
    'Bruising',
    'Swelling',
  ],
  'Code 2': [
    'Initial lumps and bumps',
    'Redness',
  ],
  'Code 3': [
    'Upset or dissatisfied client',
  ],
  'Code 4': [
    'Cold sore outbreak',
    'Prolonged or painful lumps',
    'Abnormal pain',
  ],
  'Code 5': [
    'Vascular occlusion',
    'Skin blanching or mottling',
    'Allergic reaction',
    'Visual disturbance',
    'Infection',
  ],
};

// ── New treatment types from the AE report workbook (2026-07) ──────────────
// The workbook adds 11 treatments (+ an "all treatments" cross-cutting set) as
// FLAT condition lists with NO Code 1-5 severity assigned yet — that coding is
// pending clinical/stakeholder sign-off. Until then, each treatment's conditions
// are surfaced under EVERY code (Code 1-5) so the treatment is fully testable
// regardless of the severity chosen in the wizard. When the real per-code split
// is agreed, replace `flatAcrossCodes(list)` with an explicit { 'Code n': [...] }
// map for that treatment. Condition labels are normalised to sentence case and
// stripped of the redundant treatment-name prefix used in the flat spreadsheet.
const flatAcrossCodes = (conditions) => ({
  'Code 1': conditions,
  'Code 2': conditions,
  'Code 3': conditions,
  'Code 4': conditions,
  'Code 5': conditions,
});

const SKIN_PEELS_CONDITIONS      = ['Peeling', 'Dermatitis', 'Other'];
const SKIN_TREATMENT_CONDITIONS  = ['Allergy — product known', 'Allergy — product unknown', 'Product in the eye', 'Triggering of skin condition', 'Near miss', 'Other'];
const POTENZA_CONDITIONS         = ['Inflammation', 'Hyperpigmentation', 'Grounding pad burn', 'Other'];
const HYDRAFACIAL_CONDITIONS     = ['Inflammation', 'Hyperpigmentation', 'Other'];
const MICRONEEDLING_CONDITIONS   = ['Inflammation', 'Hyperpigmentation', 'Other'];
const TATTOO_REMOVAL_CONDITIONS  = ['Blistering', 'Infection', 'Limb swelling', 'Swelling', 'Other'];
const COOLSCULPT_CONDITIONS      = ['Paradoxical adipose hyperplasia (PAH)', 'Panniculitis', 'Freeze burn', 'Hyperpigmentation', 'Excessive bruising', 'Late onset pain', 'Other'];
const EXILIS_CONDITIONS          = ['Erythema / scratch marks', 'Panniculitis', 'Electric shocks', 'Back / abdo pain', 'Other'];
const EMSCULPT_CONDITIONS        = ['Erythema', 'Electric shocks', 'Back / abdo pain', 'Other'];
const CLEAR_BRILLIANT_CONDITIONS = ['Burns', 'Irritation', 'Other'];
const EMSELLA_CONDITIONS         = ['Worsening of condition', 'Pain', 'No documentation', 'Other'];
// NOTE: the workbook's "all treatments" cross-cutting conditions (pregnant,
// breastfeeding, contraindicated, medical emergency, under age, no documentation)
// are handled as TAGS, not as a treatment type — so there is no AllTreatments
// treatment/vocabulary here.

// Nested cascade tree — the seed/fallback for admin config key
// `incidentCategoriesByTreatmentAndCode` (Phase 3). LHR is keyed by code; AWI is
// keyed by subtype then code; the new flat treatments are keyed by code.
export const INCIDENT_CATEGORIES = {
  LHR: LHR_CATEGORIES,
  AWI: { Toxin: AWI_TOXIN_CATEGORIES, Filler: AWI_FILLER_CATEGORIES },
  // New flat treatments — same condition list under every code (placeholder
  // coding, see note above).
  SkinPeels:      flatAcrossCodes(SKIN_PEELS_CONDITIONS),
  SkinTreatment:  flatAcrossCodes(SKIN_TREATMENT_CONDITIONS),
  Potenza:        flatAcrossCodes(POTENZA_CONDITIONS),
  Hydrafacial:    flatAcrossCodes(HYDRAFACIAL_CONDITIONS),
  MicroNeedling:  flatAcrossCodes(MICRONEEDLING_CONDITIONS),
  TattooRemoval:  flatAcrossCodes(TATTOO_REMOVAL_CONDITIONS),
  CoolSculpt:     flatAcrossCodes(COOLSCULPT_CONDITIONS),
  Exilis:         flatAcrossCodes(EXILIS_CONDITIONS),
  Emsculpt:       flatAcrossCodes(EMSCULPT_CONDITIONS),
  ClearBrilliant: flatAcrossCodes(CLEAR_BRILLIANT_CONDITIONS),
  Emsella:        flatAcrossCodes(EMSELLA_CONDITIONS),
};

// Treatments with a real Code 1-5 severity model. The 2026-07 workbook treatments
// are flat (no codes assigned yet), so the wizard hides the severity picker and the
// case file hides the severity badge/menu for anything not in this set.
const CODED_TREATMENTS = new Set(['LHR', 'AWI']);
export const treatmentHasCodes = (treatment) => CODED_TREATMENTS.has(treatment);

/**
 * severityLevel('Code N') → N (number). Anything else — 'Not AE', free text,
 * null — → null (fail-closed for numeric comparisons). Mirrors the server's
 * ruleEngine.severityLevelFromValue.
 */
export function severityLevel(severity) {
  if (severity == null) return null;
  const m = /^code\s*(\d+)$/i.exec(String(severity).trim());
  return m ? Number(m[1]) : null;
}

// ── Review date (Code 3–5) ────────────────────────────────────────────────
// Coded treatments at Code 3 and above record the date the client was / will
// be reviewed (required_evidence_logic.md §5). Non-coded treatments have no
// code, so never ask.
export const REVIEW_DATE_MIN_LEVEL = 3;
export function reviewDateRequired(treatment, severity) {
  if (!treatmentHasCodes(treatment)) return false;
  const level = severityLevel(severity);
  return level != null && level >= REVIEW_DATE_MIN_LEVEL;
}

/**
 * Get the incident category list for a given (treatment, subtype, code).
 * Returns [] if no vocabulary applies (e.g. treatment is "Other").
 * NOTE: hardcoded fallback — config-driven lookup lives in eventConfig.js.
 */
export function getIncidentCategories(treatment, subtype, code) {
  if (!treatment || !code) return [];
  const node = INCIDENT_CATEGORIES[treatment];
  if (!node || typeof node !== 'object') return [];
  // Nested treatment (declares subtypes, e.g. AWI → Toxin/Filler): read the
  // subtype branch. Flat treatment (LHR + the new workbook treatments): read the
  // code branch directly.
  if (subtype && node[subtype] && typeof node[subtype] === 'object') {
    return Array.isArray(node[subtype][code]) ? node[subtype][code] : [];
  }
  return Array.isArray(node[code]) ? node[code] : [];
}

// ── Attachment categories (typed upload slots) ────────────────────────────
export const ATTACHMENT_CATEGORIES = [
  { key: 'photo',                        label: 'Photos' },
  { key: 'training_certificate',         label: 'Training certificate' },
  { key: 'consent_form',                 label: 'Consent form' },
  { key: 'treating_therapist_statement', label: 'Treating therapist statement' },
  { key: 'pre_photo',                    label: 'Pre photo' },
  { key: 'post_photo',                   label: 'Post photo' },
  { key: 'dosage_chart',                 label: 'Dosage chart' },
  { key: 'laser_log_files',              label: 'Laser log files' },
  { key: 'injector_consultancy_agreement', label: 'Injector consultancy agreement' },
  { key: 'injector_insurance_policy',    label: 'Injector insurance policy' },
  { key: 'other',                        label: 'Other' },
];

export const ATTACHMENT_CATEGORY_LABELS = Object.fromEntries(
  ATTACHMENT_CATEGORIES.map(c => [c.key, c.label])
);

// Categories whose content is prose — the required slot offers a type-it-in
// pop-up that saves as a .txt attachment (uploading a signed/scanned file
// stays available from the same pop-up).
export const TYPED_TEXT_CATEGORIES = new Set(['treating_therapist_statement']);

// ── Two-step uploads: per-category metadata ───────────────────────────────
// Some categories carry facts the file itself cannot (when a post photo was
// taken; who trained whom, and when). The slot opens SlotUploadModal, which
// asks for these after the file(s) are picked; the values travel with each
// file as the `metadata` multipart field and land in attachments.metadata.
// Mirror of api/src/lib/uploadMetadata.js — keep the two in sync.
export const UPLOAD_METADATA_FIELDS = {
  post_photo: [
    { key: 'takenOn',      label: 'Date photo was taken', type: 'date', required: true },
  ],
  training_certificate: [
    { key: 'trainingDate', label: 'Training date',        type: 'date', required: true },
    { key: 'trainerName',  label: 'Trainer name',         type: 'text', required: true, max: 200 },
  ],
};

/** True when uploads of this category must go through the metadata modal. */
export function categoryNeedsMetadata(category) {
  return Array.isArray(UPLOAD_METADATA_FIELDS[category]) && UPLOAD_METADATA_FIELDS[category].length > 0;
}


// ── Requirements matrix per (treatment, code) ─────────────────────────────
// fields:         extra case fields REQUIRED for completion
// optionalFields: extra case fields offered (rendered in the same block under an
//                 "Optional" label) but never counted as outstanding
// uploads:        attachment categories that must have at least one file
// notify:         *who* needs to act/be informed — kept for downstream layer; no routing here
// priority:       'normal' | 'high'

// LHR Code 3/4 require no case FIELDS: the training date + trainer name are
// captured as upload metadata on the training_certificate file
// (UPLOAD_METADATA_FIELDS), not as type_specific fields. training_date /
// trainer_name stay in FIELD_META only so historic values still display.
const _LHR_C3_BASE = {
  fields:         [],
  optionalFields: [],
  uploads:        ['training_certificate', 'consent_form', 'treating_therapist_statement'],
  notify:         ['@trainers'],
  priority:       'normal',
};

const _LHR_C5_ADD = {
  fields:         ['client_skin_type', 'laser_settings', 'laser_serial_number', 'incident_datetime'],
  optionalFields: [],
  uploads:        ['laser_log_files'],
  notify:         ['@service'],
  priority:       'high',
};

// Every AWI code (1–5): product / area / dosage / symptoms are MANDATORY (they
// block submission — see SUBMIT_REQUIRED_FIELDS — and count as outstanding on
// the case file); the injector yes/no switch stays optional
// (required_evidence_logic.md §6, product-owner decision 2026-09-09).
const _AWI_BASE = {
  fields:         ['product_used', 'area_treated', 'dosage', 'symptoms'],
  optionalFields: ['injector_notified_acknowledgement'],
  uploads:        [],
  notify:         [],
  priority:       'normal',
};

/**
 * Matrix fields that must be filled to SUBMIT (not merely to complete) a case,
 * per treatment key. Everything else in a row's `fields` is a completion
 * requirement (LHR Code 5 laser details stay completion-only).
 */
export const SUBMIT_REQUIRED_FIELDS = Object.freeze({
  AWI: Object.freeze(['product_used', 'area_treated', 'dosage', 'symptoms']),
});
export function submitRequiredFields(treatment) {
  return SUBMIT_REQUIRED_FIELDS[treatment] || [];
}

const _AWI_C3_BASE = {
  fields:         [],
  optionalFields: [],
  uploads:        ['pre_photo', 'post_photo', 'consent_form', 'dosage_chart'],
  notify:         [],
  priority:       'normal',
};

const _AWI_C4_ADD = {
  fields:         [],
  optionalFields: [],
  uploads:        ['injector_consultancy_agreement', 'injector_insurance_policy'],
  notify:         ['People team', 'HoRO', 'Head of Injectable Growth UK & ROI', 'treating injector'],
  priority:       'high',
};

/**
 * Merge requirement records into one — used to build cumulative requirements
 * as severity escalates (Code 5 = Code 3 + Code 5-add). A key that is required
 * in any record is never also listed as optional.
 */
function _merge(...records) {
  const fields = [...new Set(records.flatMap(r => r.fields || []))];
  return {
    fields,
    optionalFields: [...new Set(records.flatMap(r => r.optionalFields || []))].filter(k => !fields.includes(k)),
    uploads:  [...new Set(records.flatMap(r => r.uploads || []))],
    notify:   [...new Set(records.flatMap(r => r.notify || []))],
    priority: records.some(r => r.priority === 'high') ? 'high' : 'normal',
  };
}

const TREATMENT_REQUIREMENTS = {
  LHR: {
    'Code 1': null,
    'Code 2': null,
    'Code 3': _LHR_C3_BASE,
    'Code 4': _LHR_C3_BASE,
    'Code 5': _merge(_LHR_C3_BASE, _LHR_C5_ADD),
  },
  AWI: {
    'Code 1': _AWI_BASE,
    'Code 2': _AWI_BASE,
    'Code 3': _merge(_AWI_BASE, _AWI_C3_BASE),
    'Code 4': _merge(_AWI_BASE, _AWI_C3_BASE, _AWI_C4_ADD),
    'Code 5': _merge(_AWI_BASE, _AWI_C3_BASE, _AWI_C4_ADD),
  },
};

// ── Clinic-side vs HQ-side requirements ─────────────────────────────────────
// Training records are held by head office (the training team uploads the
// certificate, with the training date + trainer name captured alongside the
// file as upload metadata), so the wizard never asks anyone for them — whatever
// their role. The slot still appears on the case file, where the training team
// fulfils it. HQ_ONLY_REQUIREMENT_FIELDS is now EMPTY (the two training fields
// left the matrix in 0.46) but stays exported for the wizard's filter.
// Laser log files STAY on submission for now (required_evidence_logic.md §7).
export const HQ_ONLY_REQUIREMENT_FIELDS  = new Set([]);
export const HQ_ONLY_REQUIREMENT_UPLOADS = new Set(['training_certificate']);

const _withOptional = (rec) => ({ ...rec, optionalFields: rec.optionalFields || [] });

/**
 * Get the requirements for a (treatment, code) tuple. Returns null when the
 * treatment/code has nothing to show (e.g. no treatment, or AWI without a
 * code). Every non-null result carries { fields, optionalFields, uploads,
 * notify, priority } — optionalFields defaults to [].
 *
 * NOTE: a non-null row does NOT mean something is *required* — AWI Code 1–2
 * are optional-only rows (fields: [], uploads: []). Consumers deciding whether
 * to show a "Required …" heading must test hasRequiredItems(), not truthiness.
 */
export function getRequirements(treatment, code) {
  if (!treatment) return null;
  // AWI is the exception — only its own per-code document set (no blanket Photos),
  // and it always has a code.
  if (treatment === 'AWI') {
    const row = code ? TREATMENT_REQUIREMENTS.AWI[code] : null;
    return row ? _withOptional(row) : null;
  }
  // Every other treatment requires a "Photos" upload — merged with the per-code
  // matrix for coded treatments (LHR), or on its own for the non-coded workbook
  // treatments (which carry no severity code).
  const base = code ? (TREATMENT_REQUIREMENTS[treatment]?.[code] || null) : null;
  if (!base) return { fields: [], optionalFields: [], uploads: ['photo'], notify: [], priority: 'normal' };
  return { ..._withOptional(base), uploads: [...new Set([...(base.uploads || []), 'photo'])] };
}

/**
 * Document slots the CASE FILE shows for a case: the matrix uploads for
 * (treatment, code) plus — on every Treatment case — the consent form, which
 * since 0.46 every Treatment submission must capture (upload it, or record
 * "No consent form" with a reason; lib/waivers.js) even when the matrix row
 * has no consent slot (LHR / AWI Code 1–2, non-coded workbook treatments).
 * Mirrors the wizard's submitRequiredUploads, so what was captured at
 * submission always has a slot to show in. Non-Treatment events (Near-Miss,
 * Clinic Incident) have no document slots; a missing eventType is read as
 * Treatment (legacy rows). Order: matrix order, consent slotted in ahead of
 * the Photos baseline.
 */
export function caseDocumentSlots(eventType, treatment, code) {
  if (eventType && eventType !== 'Treatment') return [];
  if (!treatment) return [];
  const uploads = getRequirements(treatment, code)?.uploads || [];
  if (uploads.includes('consent_form')) return uploads;
  const photoAt = uploads.indexOf('photo');
  return photoAt === -1
    ? [...uploads, 'consent_form']
    : [...uploads.slice(0, photoAt), 'consent_form', ...uploads.slice(photoAt)];
}

/** True when a requirements row has at least one REQUIRED field or upload. */
export function hasRequiredItems(requirements) {
  if (!requirements) return false;
  return (requirements.fields?.length || 0) > 0 || (requirements.uploads?.length || 0) > 0;
}

// ── Required-to-submit evidence (LHR Code 1–2): laser settings ─────────────
// The mild LHR codes have no per-code completion matrix, but the submission
// itself must capture the laser settings — or an explicit declaration that
// none were recorded (the no_laser_settings type_specific flag). Unlike
// getRequirements, this blocks submit. (The consent form used to sit here too;
// since 0.46 it is required on EVERY Treatment submission — upload or waiver
// with a reason, see lib/waivers.js — so it no longer hangs off this helper.)
export function submitEvidenceRequired(treatment, code) {
  return treatment === 'LHR' && (code === 'Code 1' || code === 'Code 2');
}

// ── Near Miss vocabularies ────────────────────────────────────────────────
// Starter list — pending Compliance sign-off. Eventually lives in
// admin_config under `nearMissCategories`, `nearMissReportingFunctions`,
// `nearMissAssignees`, `nearMissSeverityTiers`.

export const NEAR_MISS_CATEGORIES = [
  'Fire Safety',
  'Needles and Sharps',
  'Electrical Safety',
  'Hazardous Substance',
  'Infection Control',
  'Health and Safety',
  'Regulatory Compliance',
  'Laser Safety',
  'Other',
];

/**
 * The category list offered for a reporting function. Everything sees the base
 * list; Compliance additionally gets "Refusal Register" — the locked, self-closing
 * register entry (refusal_register_logic.md). Keeping it function-scoped stops a
 * Facilities or Clinical report being filed into a compliance register.
 */
export function nearMissCategoriesFor(reportingFunction) {
  return reportingFunction === REFUSAL_REGISTER_FUNCTION
    ? [...NEAR_MISS_CATEGORIES, REFUSAL_REGISTER_CATEGORY]
    : NEAR_MISS_CATEGORIES;
}

/**
 * Category options for the CASE FILE editor. "Refusal Register" is only offered on
 * a case that already is one (so the dropdown can show its own value) — a normal
 * near miss cannot be retro-filed into the register, because that path would
 * neither tag nor close it.
 */
export function nearMissCategoryOptions(currentCategory) {
  return currentCategory === REFUSAL_REGISTER_CATEGORY
    ? [...NEAR_MISS_CATEGORIES, REFUSAL_REGISTER_CATEGORY]
    : NEAR_MISS_CATEGORIES;
}

export const NEAR_MISS_REPORTING_FUNCTIONS = [
  { value: 'Compliance', label: 'Compliance' },
  { value: 'Facilities', label: 'Facilities' },
  { value: 'Clinical',   label: 'Clinical' },
  { value: 'Operations', label: 'Operations' },
  { value: 'Other',      label: 'Other' },
];

export const NEAR_MISS_ASSIGNEES = [
  { value: 'ClinicTeam',     label: 'Clinic team' },
  { value: 'ClinicManager',  label: 'Clinic manager' },
  { value: 'Facilities',     label: 'Facilities team' },
  { value: 'Compliance',     label: 'Compliance team' },
  { value: 'Other',          label: 'Other (specify)' },
];

export const NEAR_MISS_SEVERITY_TIERS = [
  { value: 'low',      label: 'Low',      hint: 'Minor inconvenience if it had occurred' },
  { value: 'moderate', label: 'Moderate', hint: 'Disruption or minor injury if it had occurred' },
  { value: 'high',     label: 'High',     hint: 'Serious harm, fatality, or major loss if it had occurred' },
];

export const NEAR_MISS_FIELD_META = {
  reporting_function:        { label: 'Reporting function', type: 'select' },
  near_miss_category:        { label: 'Category', type: 'select' },
  near_miss_category_other:  { label: 'Describe category', type: 'text' },
  description:               { label: 'What nearly went wrong', type: 'textarea' },
  cause:                     { label: 'Cause', type: 'textarea' },
  corrective_action:         { label: 'Corrective action', type: 'textarea' },
  assignee_role:             { label: 'Assigned to', type: 'select' },
  assignee_other:            { label: 'Specify assignee', type: 'text' },
  target_resolution_date:    { label: 'Target resolution date', type: 'date' },
  potential_severity_tier:   { label: 'Potential severity (if it had occurred)', type: 'select' },
};

// ── Clinic Incident vocabularies ──────────────────────────────────────────
// Non-treatment incident at a clinic (staff accident at work / public liability).
// Starter lists — pending Compliance & People Team sign-off. Spec:
// clinic_incident_logic.md. Eventually these move to admin_config, same as the
// nearMiss* keys (Phase 3).

// "Type of incident" — the mechanism. Maps to the case's incident_category.
export const CLINIC_INCIDENT_TYPES = [
  'Slip',
  'Trip',
  'Fall',
  'Collision / struck by object',
  'Manual handling',
  'Sharps / needlestick',
  'Exposure to substance',
  'Property / equipment damage',
  'Other',
];

// Who was hurt — drives whether this is an accident-at-work or a public-liability
// incident downstream, without needing two separate forms.
export const CLINIC_INCIDENT_PERSON_STATUS = [
  { value: 'Employee',        label: 'Employee' },
  { value: 'Contractor',      label: 'Contractor' },
  { value: 'MemberOfPublic',  label: 'Member of public' },
  { value: 'Other',           label: 'Other' },
];

// Labels + input types for the clinic-incident type_specific fields. Used by the
// wizard to render inputs and (via humanised keys) by CaseDetail to display them.
export const CLINIC_INCIDENT_FIELD_META = {
  incident_type:        { label: 'Type of incident', type: 'select' },
  incident_type_other:  { label: 'Describe the incident type', type: 'text' },
  person_status:        { label: 'Injured person', type: 'select' },
  injured_name:         { label: 'Injured person name', type: 'text' },
  injured_contact:      { label: 'Injured person contact', type: 'text' },
  description:          { label: 'What happened', type: 'textarea' },
  injury_nature:        { label: 'Nature of injuries', type: 'textarea' },
  immediate_response:   { label: 'Immediate response / first aid', type: 'textarea' },
  witness_name:         { label: 'Witness name', type: 'text' },
  witness_contact:      { label: 'Witness contact', type: 'text' },
  witness_statement:    { label: 'Witness statement', type: 'textarea' },
};

// ── Type-specific extra-field metadata ────────────────────────────────────
// Labels + input types for the "additional fields" the matrix may demand.
// Used by the wizard to render the right inputs.
//
// Types: text | select | checkbox | date | datetime-local
//        yesno    — tri-state null | true | false; rendered as a 2-segment
//                   Yes / No switch with an unselected state; displayed as
//                   'Yes' | 'No' | 'Not answered'
//        textarea — multi-line, auto-grow, soft-limit counter
// Character limits live in lib/fieldLimits.js (TYPE_SPECIFIC_LIMITS / SOFT_LIMITS).
export const FIELD_META = {
  training_date:                    { label: 'Training date', type: 'date' },
  trainer_name:                     { label: 'Trainer name', type: 'text' },
  client_skin_type:                 { label: 'Client skin type', type: 'select', options: ['I', 'II', 'III', 'IV', 'V', 'VI'] },
  laser_settings:                   { label: 'Laser settings used', type: 'text' },
  laser_serial_number:              { label: 'Laser serial number', type: 'text' },
  incident_datetime:                { label: 'Date and time of incident', type: 'datetime-local' },
  injector_notified_acknowledgement:{ label: 'Injector notified of adverse event and legal action', type: 'yesno' },
  // AWI (all codes) — optional detail about the product and the reaction.
  product_used:                     { label: 'Product used', type: 'text' },
  area_treated:                     { label: 'Area treated', type: 'text' },
  dosage:                           { label: 'Dosage', type: 'text' },
  symptoms:                         { label: 'Symptoms', type: 'textarea' },
  // Submit-evidence declaration (LHR Code 1–2) — stored only when true, so
  // the case file either shows "Yes" or omits the row entirely. (The consent
  // form declaration is a waiver now — type_specific.waivers.consent_form —
  // see lib/waivers.js; the legacy no_consent_form flag is read-compat only.)
  no_laser_settings:                { label: 'No laser settings recorded', type: 'checkbox' },
};
